import { useCallback, useEffect, useState } from "react";
import PubSub from "pubsub-js";
import Typography from "aldoo-ra/Typography";
import Button from "aldoo-ra/Button";
import DataTable from "aldoo-ra/DataTable";
import Tooltip from "aldoo-ra/Tooltip";
import {
  PaperAirplaneIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  DocumentIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { listSentTransfers } from "../ReceiveLinks/controller";
import StatusBadge from "../FileShareDashboard/StatusBadge";
import { formatRelativeTime } from "../FileShareDashboard/utils";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function renderDateCell(value) {
  if (!value) return null;
  const d = new Date(value);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const dateLabel = isToday
    ? "Today"
    : isYesterday
      ? "Yesterday"
      : d.toLocaleDateString();
  return (
    <Tooltip text={formatRelativeTime(value)} position="top">
      <div className="flex flex-col text-xs">
        <span>{dateLabel}</span>
        <span className="text-text_secondary">
          {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
    </Tooltip>
  );
}

// Sender's outbound-via-link log. Same DataTable layout the dashboard uses —
// columns + header row, status badge, open action.
export default function SentPanel({ texts }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const apiMethod = useCallback(
    async (params) => listSentTransfers(params),
    [],
  );

  // Realtime: an owner-side delete or scan flip touching one of my sent
  // transfers — refetch so the row reflects the new state.
  useEffect(() => {
    const events = ["transfer.share.deleted", "transfer.share.scan"];
    const tokens = events.map((e) =>
      PubSub.subscribe(e, () => setRefreshKey((k) => k + 1)),
    );
    return () => tokens.forEach((t) => PubSub.unsubscribe(t));
  }, []);

  const openRecipientPage = (token) => {
    if (!token) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    window.open(`${origin}/s/${token}`, "_blank", "noopener");
  };

  return (
    <div className="bg-paper border border-bordered rounded-2xl overflow-hidden p-4 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18),0_10px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-black/5">
      <DataTable
        apiMethod={apiMethod}
        refreshKey={refreshKey}
        searchField="file_name"
        searchObject={{ file_name: "" }}
        searchPlaceholder={texts?.searchSentPh}
        itemsPerPage={20}
        preferredHeight="55vh"
        styleOverrides={{
          searchInput: "ml-0 w-[340px] lg:w-[480px]",
          headerRow:
            "text-left text-text text-sm md:text-base xl:text-[18px] font-semibold align-top bg-neutral_1/40 border-b border-bordered",
          getHeaderCellStyle: (h) => `px-4 py-4 ${h || ""}`,
          getCellStyle: (c) =>
            `px-4 py-4 border-b border-neutral_1/50 ${c || ""}`,
        }}
        renders={[
          {
            id: "file_name",
            title: texts?.title,
            headerClassName: "min-w-[260px]",
            render: (_, row) => (
              <div
                className={`flex items-center gap-3 min-w-0 ${row.share_token ? "cursor-pointer" : "opacity-60"}`}
                onClick={() =>
                  row.share_token && openRecipientPage(row.share_token)
                }
              >
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <DocumentIcon className="w-5 h-5" />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text truncate">
                      {row.title || row.file_name}
                    </span>
                    {row.scan_pending && (
                      <span className="text-xs bg-warning/10 text-warning rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <ShieldCheckIcon className="w-3 h-3" />
                        {texts?.scanning}
                      </span>
                    )}
                    {row.deleted_by_owner_at && (
                      <span className="text-xs bg-error/10 text-error rounded-full px-2 py-0.5">
                        {texts?.deletedByRecipient}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text_secondary truncate">
                    {row.file_count}{" "}
                    {row.file_count === 1 ? texts?.file : texts?.files}
                    {" · "}
                    {formatBytes(row.file_size)}
                    {row.receiver_link_alias
                      ? ` · ${row.receiver_link_alias}`
                      : ""}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: "recipient_owner_name",
            title: texts?.recipient,
            headerClassName: "w-[160px]",
            render: (name) => (
              <span className="text-sm text-text truncate">{name || "—"}</span>
            ),
          },
          {
            id: "created",
            title: texts?.date,
            headerClassName: "w-[100px]",
            render: (d) => renderDateCell(d),
          },
          {
            id: "status",
            title: texts?.status,
            headerClassName: "w-[110px]",
            render: (status) => <StatusBadge status={status} />,
          },
          {
            id: "actions",
            title: "",
            headerClassName: "w-[60px]",
            render: (_, row) =>
              row.share_token ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openRecipientPage(row.share_token);
                  }}
                  className="p-1.5 rounded-lg text-text_secondary hover:text-text hover:bg-neutral_1 transition-colors"
                  title={texts?.open}
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </button>
              ) : null,
          },
        ]}
        itemIdField="orderID"
        itemHeight="h-auto min-h-[64px]"
        tableHeader={<div className="h-2" />}
        noData={
          <div className="flex flex-col items-center gap-4 py-8">
            <PaperAirplaneIcon className="h-12 w-12 text-text_secondary" />
            <Typography variant="h4" className="text-text_secondary">
              {texts?.noSent}
            </Typography>
          </div>
        }
      />
    </div>
  );
}
